import { z } from 'zod';
import {
  TenantIdSchema,
  UlidSchema,
  MontoClpSchema,
  PeriodoSchema,
  IsoDateSchema,
  IsoTimestampSchema,
} from './common.js';

/**
 * Gasto Común — cabecera de un período de cobro.
 * Cada gasto común genera N CargoUnidad (uno por unidad activa).
 */

export const EstadoGastoComunSchema = z.enum([
  'borrador',
  'emitido',
  'parcialmente_pagado',
  'pagado',
  'anulado',
]);
export type EstadoGastoComun = z.infer<typeof EstadoGastoComunSchema>;

export const CategoriaGastoSchema = z.enum([
  'administracion',
  'mantencion',
  'aseo',
  'seguridad',
  'servicios_basicos',
  'seguros',
  'fondo_reserva',
  'extraordinario',
  'otros',
]);
export type CategoriaGasto = z.infer<typeof CategoriaGastoSchema>;

export const LineaGastoSchema = z.object({
  categoria: CategoriaGastoSchema,
  concepto: z.string().min(3).max(200),
  monto: MontoClpSchema,
});
export type LineaGasto = z.infer<typeof LineaGastoSchema>;

export const GastoComunSchema = z.object({
  id: UlidSchema,
  tenantId: TenantIdSchema,
  periodo: PeriodoSchema,
  lineas: z.array(LineaGastoSchema).min(1),
  montoTotal: MontoClpSchema,
  fechaEmision: IsoDateSchema,
  fechaVencimiento: IsoDateSchema,
  estado: EstadoGastoComunSchema,
  /** Modalidad de prorrateo. MVP soporta solo alícuota. */
  modoPrororrateo: z.literal('alicuota').default('alicuota'),
  createdAt: IsoTimestampSchema,
  updatedAt: IsoTimestampSchema,
});
export type GastoComun = z.infer<typeof GastoComunSchema>;

/** Cargo por unidad — resultado del prorrateo. */
export const EstadoCargoSchema = z.enum(['pendiente', 'pagado', 'vencido', 'parcial']);
export type EstadoCargo = z.infer<typeof EstadoCargoSchema>;

/** Estado extendido de CargoUnidad — incluye anulado/reembolsado. */
export const EstadoCargoUnidadSchema = z.enum([
  'pendiente',
  'pagado',
  'vencido',
  'anulado',
  'reembolsado',
]);
export type EstadoCargoUnidad = z.infer<typeof EstadoCargoUnidadSchema>;

export const CargoUnidadSchema = z.object({
  id: UlidSchema,
  tenantId: TenantIdSchema,
  gastoComunId: UlidSchema,
  unidadId: UlidSchema,
  montoBase: MontoClpSchema,
  montoMora: MontoClpSchema.default(0),
  montoPagado: MontoClpSchema.default(0),
  estado: EstadoCargoUnidadSchema,
  fechaVencimiento: IsoDateSchema,
  createdAt: IsoTimestampSchema,
  updatedAt: IsoTimestampSchema,
});
export type CargoUnidad = z.infer<typeof CargoUnidadSchema>;

export const EmitirGastoComunRequestSchema = z.object({
  periodo: PeriodoSchema,
  lineas: z.array(LineaGastoSchema).min(1),
  fechaVencimiento: IsoDateSchema,
});
export type EmitirGastoComunRequest = z.infer<typeof EmitirGastoComunRequestSchema>;

export const EmitirGastoComunResponseSchema = z.object({
  gastoComun: GastoComunSchema,
  cargosGenerados: z.array(CargoUnidadSchema),
});
export type EmitirGastoComunResponse = z.infer<typeof EmitirGastoComunResponseSchema>;

export const ActualizarBorradorRequestSchema = z.object({
  lineas: z.array(LineaGastoSchema).min(1).optional(),
  fechaVencimiento: IsoDateSchema.optional(),
});
export type ActualizarBorradorRequest = z.infer<typeof ActualizarBorradorRequestSchema>;

export const AnularGastoComunRequestSchema = z.object({
  motivo: z.string().min(10).max(500),
});
export type AnularGastoComunRequest = z.infer<typeof AnularGastoComunRequestSchema>;
